import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@vitness/theme';
import { ClerkProvider } from '@clerk/react';
import { requireClerkPublishableKey } from '@vitness/auth';
// Token CSS lives in the vendored package. Imported by relative path because the
// Vite alias is exact-match and the package's `exports` map points at `dist/`,
// which we deliberately didn't vendor.
import '../packages/tokens/src/css/tokens.css';
import '../packages/tokens/src/css/tokens.dark.css';
import '../packages/tokens/src/css/tokens.light.css';
import './tailwind.css';
import './app.css';
import { App } from './App';
import { applyUiScale, readStoredScale } from './lib/useUiScale';

// Before first paint, or the whole interface visibly resizes a frame in.
// `readStoredScale` picks the row for this device class (phone 100%, desktop
// 125%) — the defaults differ, so getting this wrong is a visible jump, not
// just a stale number.
applyUiScale(readStoredScale());

// The publishable key is public by design — it identifies the Clerk instance to
// the browser and carries no authority. It still has to be *present*, and
// failing loudly here beats a blank screen and a console warning.
const clerkPublishableKey = requireClerkPublishableKey(
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined,
);

// ClerkProvider sits outside ThemeProvider so the sign-in screen can read the
// theme (it renders inside), but Clerk's own session bootstrap doesn't depend on
// a theme being resolved first.
// Defaults (dark · green · outlined) live in packages/theme/ThemeProvider.tsx.
// D1 is the source of truth once bootstrap lands — see lib/usePreferences.ts.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider publishableKey={clerkPublishableKey} afterSignOutUrl="/">
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </ClerkProvider>
  </StrictMode>,
);
